/**
 * Recursively patches `current` with values from `next`, preserving references
 * for sub-trees that haven't changed. This prevents unnecessary signal
 * re-emissions in Angular signal forms when only part of the model changes.
 *
 * - Primitives: returns `next` (no freezing concern).
 * - Objects/Arrays: rebuilds only the branches that differ, keeps `current` ref otherwise.
 * - Never returns frozen store references — any subtree pulled wholesale from
 *   `next` (a new key, a new/extra array item, or a type-mismatch replacement)
 *   is `deepClone`d so the form model only ever holds mutable objects.
 */
export function deepPatch<T>(current: T, next: T): T {
    if (Object.is(current, next)) {
        return current;
    }

    if (current == null || next == null || typeof current !== 'object' || typeof next !== 'object') {
        return cloneMutable(next);
    }

    if (Array.isArray(next)) {
        if (!Array.isArray(current)) {
            return cloneMutable(next);
        }

        const result = next.map((item, i) => (i < current.length ? deepPatch(current[i], item) : cloneMutable(item)));

        if (result.length === current.length && result.every((item, i) => item === current[i])) {
            return current;
        }

        return result as T;
    }

    const currentObj = current as Record<string, any>;
    const nextObj = next as Record<string, any>;
    const nextKeys = Object.keys(nextObj);

    let changed = nextKeys.length !== Object.keys(currentObj).length;
    const result: Record<string, any> = {};

    for (const key of nextKeys) {
        if (key in currentObj) {
            result[key] = deepPatch(currentObj[key], nextObj[key]);
            if (result[key] !== currentObj[key]) {
                changed = true;
            }
        } else {
            result[key] = cloneMutable(nextObj[key]);
            changed = true;
        }
    }

    return changed ? (result as T) : current;
}

function cloneMutable<T>(value: T): T {
    if (value === null || typeof value !== 'object') {
        return value;
    }

    if (Array.isArray(value)) {
        return value.map((item) => cloneMutable(item)) as T;
    }

    const source = value as Record<string, any>;
    const out: Record<string, any> = {};
    for (const key of Object.keys(source)) {
        out[key] = cloneMutable(source[key]);
    }
    return out as T;
}
